import {isFunction, each, isArray} from 'lodash';
import {useCallback, useMemo, useState} from 'react';
import {useInterval} from '@huse/timeout';
import {toast} from 'acud';

import useFetch, {FetchPayload} from '../fetch';
import {getErrorMessage} from '../../../utils/bizUtil';
import {useLocale} from '../../../components/locale';

export interface IRequest {
    action: <TData>(params?: any) => Promise<TData>,
    defaultListParams?: any;
    $onRequest?: (payload?: any, extra?: any) => any,
    $onResponse?: (result?: any, params?: any) => any,
    $onError?: (ex: any) => void,
    errorToast?: boolean;
    abort?: boolean;
    interval?: number;
}

const useTableFetch = (request: IRequest) => {
    const {action, defaultListParams, $onRequest, $onResponse, $onError, errorToast, abort, interval} = request;

    const [locale] = useLocale('useTable');

    const [listParams, setListParams] = useState(defaultListParams);

    const params = useMemo(() => {
        let actualParams = {};
        // 过滤掉空值
        each(listParams, (value, key) => {
            const isEmptyValue = value === '' || value === null || (isArray(value) && value.length === 0);
            if (!isEmptyValue) {
                actualParams[key] = value;
            }
        });
        return actualParams;
    }, [listParams]);

    const $onFetchRequest = useCallback((params, extra) => {
        // 为了不让业务侧直接修改params对象，以免造成数据对比失败，这里做一个浅拷贝（因为对比目前也只是浅层对比）
        const requestParams = {...params};
        const requestPayload = isFunction($onRequest)
            ? ($onRequest(requestParams, extra) || requestParams)
            : requestParams;
        return requestPayload;
    }, [$onRequest]);

    const $onFetchResponse = useCallback((response: any) => {
        const responsePage = isFunction($onResponse)
            ? ($onResponse(response, listParams) || response)
            : response;
        const {totalCount, pageNo, result} = responsePage;
        // 特殊逻辑，当totalCount > 0 但是没有数据的时候，查找上一页, 主要场景：最后一页数据全部删除后的情况下兼容
        if (totalCount > 0 && pageNo > 1 && !result.length) {
            setListParams((prevListParams: any) => ({...prevListParams, pageNo: pageNo - 1}));
        }
        return responsePage;
    }, [$onResponse, listParams]);

    const $onFetchError = useCallback((err: any) => {
        if (isFunction($onError)) {
            $onError(err);
        }
        const errorMsg = getErrorMessage(err, locale?.failedToGetRecords);
        errorMsg && errorToast && toast.error({
            message: errorMsg,
            duration: 5
        });
    }, [errorToast, locale?.failedToGetRecords, $onError]);

    const fetchResult = useFetch(action, params, $onFetchRequest, $onFetchResponse, $onFetchError, abort);

    // isPolling：是否是轮询请求，用以和首次请求、手动reload区分，默认为false
    const reloadParams = {needLoading: false, extra: {isPolling: true}} as FetchPayload;
    useInterval(() => fetchResult.reload(reloadParams), (interval || -1));

    return {
        listParams,
        setListParams,
        ...fetchResult
    };
};

export default useTableFetch;